import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';
import MonthlyReport from '../components/MonthlyReport';
import { ArrowLeft, BarChart3, Brain, Calendar, ChevronDown, ClipboardList, Download, Eye, FileText, Home, Phone, RefreshCw, Shield, User, UserCheck, Users } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalMonthlyReports = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [reportData, setReportData] = useState(null);
  const [viewMode, setViewMode] = useState('principal');
  const [generating, setGenerating] = useState(false);

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7); // YYYY-MM
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = getMonthOptions();

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    
    // Set default month to current month
    setSelectedMonth(monthOptions[0].value);
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(`${BACKEND_URL}/api/principal/children`, {
        params: { token }
      });
      setChildren(response.data.children || []);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load children", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedChild || !selectedMonth) {
      toast({ title: "Error", description: "Please select a child and month", variant: "destructive" });
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(`${BACKEND_URL}/api/principal/monthly-report`, {
        params: { 
          token, 
          child_id: selectedChild.id,
          month: selectedMonth
        },
        timeout: 120000 // 2 minute timeout for AI generation
      });
      setReportData(response.data);
      
      // Check if AI generation was successful
      if (response.data.ai_generated) {
        toast({ title: "Success", description: "AI-powered monthly report generated" });
      } else if (response.data.metadata?.transcripts_available > 0) {
        toast({ 
          title: "Report Generated", 
          description: "Report structure ready. AI insights pending (budget or processing issue).",
          variant: "default" 
        });
      } else {
        toast({ 
          title: "Report Generated", 
          description: `No session transcripts found for ${response.data.metadata?.month || 'this month'}`,
          variant: "default" 
        });
      }
    } catch (error) {
      console.error('Generate report error:', error);
      
      // Generate sample data only if API completely fails
      const sampleReport = generateSampleReport(selectedChild, selectedMonth);
      setReportData(sampleReport);
      toast({ title: "Info", description: "Showing sample report (API error or timeout)" });
    } finally {
      setGenerating(false);
    }
  };

  // Generate sample report data
  const generateSampleReport = (child, month) => {
    const monthLabel = new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    return {
      childName: child.name,
      month: monthLabel,
      totalCalls: 14,
      diagnostics: {
        emotional: {
          rating: 'Good',
          indicators: [
            { name: 'Self-awareness', lookFor: 'Can identify and name feelings', evidence: 'Used words like "frustrated," "excited," "happy"' },
            { name: 'Emotional vocabulary', lookFor: 'Variety of emotion words', evidence: 'Moderate-high range across conversations' },
            { name: 'Emotional regulation', lookFor: 'How they handle difficult feelings', evidence: 'Generally processes well, occasionally needs support' }
          ],
          output: `${child.name.split(' ')[0]} demonstrates good emotional awareness and vocabulary. They can identify feelings in themselves and others. Growth area: May benefit from more opportunities to express and process complex emotions.`
        },
        communication: {
          rating: 'Excellent',
          indicators: [
            { name: 'Narrative ability', lookFor: 'Can tell coherent story', evidence: 'Strong storytelling with clear sequence' },
            { name: 'Vocabulary', lookFor: 'Range and appropriateness', evidence: 'Age-appropriate with some advanced words' },
            { name: 'Confidence', lookFor: 'Speaks without hesitation', evidence: 'High when discussing interests' }
          ],
          output: `${child.name.split(' ')[0]} is an articulate communicator, especially when discussing topics they care about. Storytelling ability is a particular strength.`
        },
        social: {
          rating: 'Good',
          indicators: [
            { name: 'Peer relationships', lookFor: 'Quality of friendships', evidence: 'Mentions friends regularly, plays often' },
            { name: 'Social awareness', lookFor: 'Understanding of dynamics', evidence: 'Shows awareness of peer interactions' }
          ],
          output: `${child.name.split(' ')[0]} has healthy, active peer relationships. They understand social dynamics and navigate friendships well.`
        },
        academic: {
          rating: 'Good',
          indicators: [
            { name: 'Subjects of interest', lookFor: 'What they enjoy', evidence: 'Shows interest in creative subjects' },
            { name: 'Homework management', lookFor: 'How they handle workload', evidence: 'Generally responsible with tasks' }
          ],
          output: `${child.name.split(' ')[0]} is academically engaged with particular interest in creative subjects. Maintains good homework habits.`
        },
        family: {
          rating: 'Excellent',
          indicators: [
            { name: 'Primary attachments', lookFor: 'Who they mention', evidence: 'Frequently mentions family members positively' },
            { name: 'Home as safe base', lookFor: 'Sense of security', evidence: 'Clear comfort in home environment' }
          ],
          output: `${child.name.split(' ')[0]} has secure, warm family attachments. Strong family foundation is a protective factor.`
        },
        selfRegulation: {
          rating: 'Developing',
          indicators: [
            { name: 'Response to disappointment', lookFor: 'How they handle setbacks', evidence: 'Can reframe most situations positively' },
            { name: 'Frustration tolerance', lookFor: 'Handling things that don\'t work', evidence: 'Sometimes needs support with frustration' }
          ],
          output: `${child.name.split(' ')[0]} demonstrates developing resilience. Can reframe disappointments but may benefit from support with frustration management.`
        },
        interests: {
          rating: 'Excellent',
          indicators: [
            { name: 'Creative interests', lookFor: 'Art, music, etc.', evidence: 'Shows strong creative inclinations' },
            { name: 'Physical activities', lookFor: 'Sports, play', evidence: 'Enjoys active play with friends' }
          ],
          output: `${child.name.split(' ')[0]} has diverse interests spanning creative and physical domains. Encourage exploration of expressed interests.`
        },
        wellbeing: {
          rating: 'Good',
          indicators: [
            { name: 'General health', lookFor: 'Illness patterns', evidence: 'Generally healthy with minor seasonal issues' },
            { name: 'Activity level', lookFor: 'Physical activity', evidence: 'Active when well' }
          ],
          output: `${child.name.split(' ')[0]} maintains good general health and activity levels. No significant concerns noted.`
        }
      },
      parentReport: {
        snapshot: `${child.name.split(' ')[0]} had an engaging month with consistent participation in daily calls. They demonstrated strong communication skills, emotional awareness, and maintained healthy peer relationships. Key themes included creative interests, family connection, and growing independence.`,
        brightSpots: [
          'Strong storytelling abilities - can narrate experiences with detail and emotion',
          'Healthy friendships and social connections',
          'Creative interests in art and imaginative play',
          'Warm family relationships and secure attachment',
          'Good emotional vocabulary and self-awareness'
        ],
        growthAreas: [
          { area: 'Self-expression', observation: 'Sometimes brief when discussing feelings', suggestion: 'Ask follow-up questions about emotions' },
          { area: 'Frustration handling', observation: 'Can get frustrated with challenges', suggestion: 'Model calm problem-solving approaches' },
          { area: 'Help-seeking', observation: 'May hesitate to ask for help', suggestion: 'Explicitly encourage asking questions' }
        ],
        emotionalLandscape: {
          emotions: {
            'Joy': 'Family time, play with friends, creative activities',
            'Excitement': 'Special events, new activities',
            'Frustration': 'Challenging tasks, peer conflicts'
          },
          pattern: 'Generally positive emotional expression with healthy processing of challenges'
        },
        conversationStarters: [
          'What was the most fun thing you did this month?',
          'Who did you play with the most? What do you like about them?',
          'Is there anything new you\'d like to try?',
          'What made you laugh this week?',
          'If you could teach me something, what would it be?'
        ],
        recommendedActions: [
          'Continue encouraging storytelling - ask them to narrate experiences',
          'Celebrate creative achievements and interests',
          'Create opportunities for emotional expression through art or play',
          'Schedule quality one-on-one time',
          'Ask about friendships and peer experiences'
        ]
      },
      childReport: {
        achievements: [
          'Had amazing conversations every day!',
          'Shared wonderful stories about your experiences',
          'Showed how creative you are',
          'Made your friends and family proud',
          'Learned new things and shared them'
        ],
        superpowers: [
          { name: 'Storytelling', emoji: '📚', description: 'You tell the best stories!' },
          { name: 'Creativity', emoji: '🎨', description: 'You have amazing ideas!' },
          { name: 'Friendship', emoji: '💕', description: 'You\'re a wonderful friend!' },
          { name: 'Curiosity', emoji: '🔍', description: 'You love learning new things!' }
        ],
        memorableShares: [
          'Your exciting adventures with friends',
          'Creative projects and artwork',
          'Fun times with family',
          'New things you learned'
        ],
        highlights: [
          'Special moments with family',
          'Fun play times with friends',
          'Creative achievements'
        ],
        nextMonthAdventure: 'More amazing conversations, new stories to share, and exciting things to learn together!'
      },
      observerReport: {
        whatWorked: [
          { moment: 'Following up on emotions', reason: 'Created space for deeper sharing' },
          { moment: 'Asking about interests', reason: 'Child became animated and engaged' },
          { moment: 'Warm closing', reason: 'Ended calls on positive note' }
        ],
        opportunities: [
          { moment: 'Emotional cue', whatHappened: 'Moved on quickly', alternative: 'Pause and ask "Tell me more about that feeling"' },
          { moment: 'Frustration expressed', whatHappened: 'Gave advice', alternative: 'Explore the feeling first before problem-solving' }
        ],
        childGuidance: [
          'When they mention a feeling word - pause and explore it',
          'Follow their interests with genuine curiosity',
          'Give explicit affirmation before ending calls',
          'Allow silences - they often lead to deeper shares'
        ],
        suggestedPrompts: [
          'Tell me more about that',
          'How did that make you feel?',
          'What was that like for you?',
          'That sounds [hard/exciting/fun] - what happened next?'
        ]
      },
      warningFlags: []
    };
  };

  const exportReport = (type) => {
    toast({ title: "Export", description: `${type.charAt(0).toUpperCase() + type.slice(1)} report export coming soon!` });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="p-6 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-sanjaya-navy animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/principal/dashboard')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-slate-800">Monthly Reports & Diagnostics</h1>
          <p className="text-slate-600">Comprehensive developmental insights from daily conversations</p>
        </div>

        {/* Selection Panel */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Select Child</label>
                <select
                  value={selectedChild?.id || ''}
                  onChange={(e) => {
                    const child = children.find(c => c.id === e.target.value);
                    setSelectedChild(child);
                    setReportData(null);
                  }}
                  className="w-full border rounded-md px-3 py-2"
                  data-testid="select-child"
                >
                  <option value="">-- Select Child --</option>
                  {children.map(child => (
                    <option key={child.id} value={child.id}>{child.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Select Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setReportData(null);
                  }}
                  className="w-full border rounded-md px-3 py-2"
                  data-testid="select-month"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <Button 
                onClick={generateReport}
                disabled={!selectedChild || !selectedMonth || generating}
                className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-4 h-4 mr-2" /> Generate Report
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* View Mode Selector */}
        {reportData && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">View as:</span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {[
                { id: 'principal', label: 'Principal', icon: BarChart3 },
                { id: 'parent', label: 'Parent', icon: User },
                { id: 'child', label: 'Child', icon: Users },
                { id: 'observer', label: 'Observer', icon: Eye }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === mode.id 
                      ? 'bg-white shadow text-sanjaya-navy font-medium' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <mode.icon className="w-4 h-4" />
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Report Display */}
        {reportData ? (
          <MonthlyReport
            childName={reportData.childName}
            month={reportData.month}
            totalCalls={reportData.totalCalls}
            diagnostics={reportData.diagnostics}
            parentReport={reportData.parentReport}
            childReport={reportData.childReport}
            observerReport={reportData.observerReport}
            warningFlags={reportData.warningFlags}
            viewMode={viewMode}
            onExport={exportReport}
          />
        ) : (
          <Card className="border-dashed border-2">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Report Generated</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Select a child and month, then click "Generate Report" to create a comprehensive monthly diagnostic report.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PrincipalMonthlyReports;
